import { useState, useEffect } from 'react';
import { applyPageMeta, SITE_URL } from '@/lib/pageMeta';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

function SectionBadge({ text }: { text: string }) {
  return (
    <span className="inline-block bg-[#B80E28]/15 text-[#B80E28] text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
      {text}
    </span>
  );
}

const kinaFeatures = [
  {
    icon: 'ri-bank-line',
    title: 'PNG\'s Largest Commercial Bank',
    desc: 'Kina Bank is one of Papua New Guinea\'s leading financial institutions, offering comprehensive banking services to individuals and businesses across the country.',
  },
  {
    icon: 'ri-exchange-dollar-line',
    title: 'Instant PGK Deposits',
    desc: 'Fund your PNGGO account instantly using Kina Bank online banking or mobile app. Transfers are processed in real-time with zero delays.',
  },
  {
    icon: 'ri-time-line',
    title: 'Fast Withdrawals',
    desc: 'Withdraw your winnings directly to your Kina Bank account. Most withdrawals are completed within 5 minutes during business hours.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Bank-Grade Security',
    desc: 'All transactions are protected by Kina Bank\'s enterprise-level encryption and fraud detection systems, ensuring your funds are always safe.',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Kina Mobile Banking',
    desc: 'Use the Kina Bank mobile app to deposit and withdraw on the go. Available on iOS and Android, 24 hours a day, 7 days a week.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Support',
    desc: 'Kina Bank\'s customer service team is available to assist with any transaction queries, ensuring a smooth banking experience for all PNGGO players.',
  },
];

const bspFeatures = [
  {
    icon: 'ri-building-2-line',
    title: 'Bank of South Pacific',
    desc: 'BSP is the largest bank in the Pacific region, with an extensive network of branches and ATMs across Papua New Guinea and the wider Pacific.',
  },
  {
    icon: 'ri-wifi-line',
    title: 'BSP Internet Banking',
    desc: 'Deposit to PNGGO directly through BSP\'s secure internet banking portal. Simple, fast, and available around the clock from any device.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'PGK Currency Support',
    desc: 'All transactions are processed in Papua New Guinea Kina (PGK), eliminating currency conversion fees and ensuring transparent pricing.',
  },
  {
    icon: 'ri-lock-2-line',
    title: 'Two-Factor Authentication',
    desc: 'BSP\'s advanced 2FA system adds an extra layer of security to every transaction, protecting your funds from unauthorised access.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Nationwide Branch Network',
    desc: 'With branches in Port Moresby, Lae, Mount Hagen, Madang, and across all provinces, BSP is accessible to players throughout PNG.',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'BSP PayWave & EFTPOS',
    desc: 'Use BSP\'s PayWave and EFTPOS services for seamless deposits. Contactless payments are processed instantly with no additional fees.',
  },
];

const depositStepsKina = [
  { step: '01', title: 'Log in to PNGGO', desc: 'Sign in to your PNGGO account and navigate to the Deposit section in your account dashboard.' },
  { step: '02', title: 'Select Kina Bank', desc: 'Choose "Kina Bank" from the list of available payment methods on the deposit page.' },
  { step: '03', title: 'Enter Amount', desc: 'Enter the amount you wish to deposit in PGK. Minimum deposit is PGK 10.' },
  { step: '04', title: 'Confirm via Kina App', desc: 'You will be redirected to Kina Bank\'s secure portal. Log in and confirm the transfer.' },
  { step: '05', title: 'Funds Credited', desc: 'Your PNGGO balance is updated instantly. Start playing immediately!' },
];

const depositStepsBSP = [
  { step: '01', title: 'Log in to PNGGO', desc: 'Sign in to your PNGGO account and navigate to the Deposit section in your account dashboard.' },
  { step: '02', title: 'Select BSP', desc: 'Choose "BSP" from the list of available payment methods on the deposit page.' },
  { step: '03', title: 'Enter Amount', desc: 'Enter the amount you wish to deposit in PGK. Minimum deposit is PGK 10.' },
  { step: '04', title: 'Confirm via BSP Portal', desc: 'You will be redirected to BSP\'s secure internet banking portal. Authenticate and approve the payment.' },
  { step: '05', title: 'Funds Credited', desc: 'Your PNGGO balance is updated instantly. Start playing immediately!' },
];

const comparisonData = [
  { feature: 'Currency', kina: 'PGK', bsp: 'PGK' },
  { feature: 'Deposit Speed', kina: 'Instant', bsp: 'Instant' },
  { feature: 'Withdrawal Speed', kina: 'Within 5 mins', bsp: 'Within 5 mins' },
  { feature: 'Minimum Deposit', kina: 'PGK 10', bsp: 'PGK 10' },
  { feature: 'Maximum Deposit', kina: 'PGK 50,000', bsp: 'PGK 50,000' },
  { feature: 'Transaction Fees', kina: 'Free', bsp: 'Free' },
  { feature: 'Mobile App', kina: 'Yes', bsp: 'Yes' },
  { feature: 'Internet Banking', kina: 'Yes', bsp: 'Yes' },
  { feature: '24/7 Availability', kina: 'Yes', bsp: 'Yes' },
  { feature: 'Branch Network', kina: 'Nationwide', bsp: 'Nationwide + Pacific' },
];

const faqs = [
  {
    q: 'Are there any fees for depositing or withdrawing via Kina Bank or BSP?',
    a: 'No. PNGGO does not charge any fees for deposits or withdrawals made through Kina Bank or BSP. Your bank may apply standard transaction fees as per their own fee schedule — please check with your bank for details.',
  },
  {
    q: 'How long does a withdrawal to my Kina Bank or BSP account take?',
    a: 'Most withdrawals are processed within 5 minutes. In rare cases during peak periods or outside business hours, withdrawals may take up to 24 hours. We always aim to process your funds as quickly as possible.',
  },
  {
    q: 'Is there a minimum or maximum deposit amount?',
    a: 'The minimum deposit is PGK 10 for both Kina Bank and BSP. The maximum single deposit is PGK 50,000. If you need to deposit a larger amount, please contact our 24/7 customer support team.',
  },
  {
    q: 'Can I use a BSP or Kina Bank account that is not in my name?',
    a: 'No. For security and compliance reasons, you must only use bank accounts registered in your own name. Using a third-party account may result in your transaction being declined and your account being reviewed.',
  },
  {
    q: 'What should I do if my deposit does not appear in my PNGGO account?',
    a: 'If your deposit does not appear within 15 minutes, please contact our 24/7 live chat support with your bank transaction reference number. Our team will investigate and resolve the issue promptly.',
  },
  {
    q: 'Is my banking information safe with PNGGO?',
    a: 'Absolutely. PNGGO uses 128-bit SSL encryption and never stores your full banking credentials. All payment processing is handled directly by Kina Bank and BSP through their own secure portals — we never see your banking password.',
  },
];

const PAYMENT_TITLE = 'PNGGO Payment Methods – Kina Bank & BSP | Fast PGK Deposits & Withdrawals';
const PAYMENT_DESCRIPTION =
  'Deposit and withdraw in PGK at PNGGO using Kina Bank or BSP. Instant deposits, withdrawals under 5 minutes, zero fees, and bank-grade security for PNG players.';
const PAYMENT_KEYWORDS =
  'PNGGO deposit, PNGGO withdrawal, Kina Bank PGK, BSP casino deposit, Papua New Guinea banking casino, PGK withdrawals';

const paymentJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/payment#webpage`,
      "url": `${SITE_URL}/payment`,
      "name": PAYMENT_TITLE,
      "description": PAYMENT_DESCRIPTION,
      "inLanguage": "en",
      "isPartOf": {
        "@id": `${SITE_URL}/#website`
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Are there any fees for depositing or withdrawing via Kina Bank or BSP?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. PNGGO does not charge any fees for deposits or withdrawals made through Kina Bank or BSP. Your bank may apply standard transaction fees as per their own fee schedule."
          }
        },
        {
          "@type": "Question",
          "name": "How long does a withdrawal to my Kina Bank or BSP account take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most withdrawals are processed within 5 minutes. In rare cases during peak periods or outside business hours, withdrawals may take up to 24 hours."
          }
        },
        {
          "@type": "Question",
          "name": "Is there a minimum or maximum deposit amount?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The minimum deposit is PGK 10 for both Kina Bank and BSP. The maximum single deposit is PGK 50,000."
          }
        },
        {
          "@type": "Question",
          "name": "Can I use a BSP or Kina Bank account that is not in my name?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. For security and compliance reasons, you must only use bank accounts registered in your own name."
          }
        },
        {
          "@type": "Question",
          "name": "What should I do if my deposit does not appear in my PNGGO account?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "If your deposit does not appear within 15 minutes, please contact our 24/7 live chat support with your bank transaction reference number."
          }
        },
        {
          "@type": "Question",
          "name": "Is my banking information safe with PNGGO?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. PNGGO uses 128-bit SSL encryption and never stores your full banking credentials. All payment processing is handled directly by Kina Bank and BSP through their own secure portals."
          }
        }
      ]
    }
  ]
};

export default function PaymentPage() {
  const [activeBank, setActiveBank] = useState<'kina' | 'bsp'>('kina');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    applyPageMeta({
      title: PAYMENT_TITLE,
      description: PAYMENT_DESCRIPTION,
      path: '/payment',
      keywords: PAYMENT_KEYWORDS,
      ogDescription: PAYMENT_DESCRIPTION,
      twitterDescription:
        'Kina Bank & BSP: instant PGK deposits and fast withdrawals at PNGGO — zero fees from us, secure banking for Papua New Guinea players.',
    });
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'payment-jsonld';
    script.text = JSON.stringify(paymentJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('payment-jsonld');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=Papua%20New%20Guinea%20modern%20banking%20financial%20technology%20digital%20payment%20secure%20transaction%20dark%20background%20glowing%20red%20accents%20professional%20corporate%20photography%20wide%20angle&width=1440&height=600&seq=payment-hero-01&orientation=landscape')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/65 to-[#0a0a0a]" />
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <SectionBadge text="Payment Methods" />
          <h1 className="text-white text-4xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Fast, Secure &amp;<br />
            <span className="text-[#B80E28]">Local Banking</span>
          </h1>
          <p className="text-white/65 text-base md:text-lg max-w-2xl mx-auto leading-7 mb-10">
            PNGGO supports Papua New Guinea&apos;s most trusted banking institutions — Kina Bank and BSP. Deposit and withdraw in PGK with zero fees, instant processing, and bank-grade security.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://pnggo.bet/RFLANDINGPAGEPNG"
              className="inline-flex items-center gap-2 bg-[#B80E28] text-white font-bold px-8 py-3 rounded-full hover:bg-[#9a0b21] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Open Account <i className="fas fa-chevron-right text-xs"></i>
            </a>
            <a
              href="/"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-8 py-3 rounded-full hover:border-[#B80E28] hover:text-[#B80E28] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Back to Home
            </a>
          </div>
        </div>
      </section>

      {/* ── Quick Stats ── */}
      <section className="bg-[#111] border-y border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: 'Instant', label: 'Deposit Speed' },
            { value: '&lt; 5 Min', label: 'Withdrawal Time' },
            { value: 'PGK', label: 'Local Currency' },
            { value: '0 Fees', label: 'Transaction Charges' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <div
                className="text-[#B80E28] text-3xl md:text-4xl font-extrabold mb-1"
                dangerouslySetInnerHTML={{ __html: s.value }}
              />
              <div className="text-white/50 text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Bank Selector Tabs ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionBadge text="Our Banking Partners" />
            <h2 className="text-white text-3xl md:text-4xl font-bold mb-4">Choose Your Bank</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto">
              PNGGO is integrated with Papua New Guinea&apos;s two most prominent banks. Select your bank below to learn more.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-12">
            <div className="bg-[#1a1a1a] rounded-full p-1 flex gap-1">
              <button
                onClick={() => setActiveBank('kina')}
                className={`px-8 py-2.5 rounded-full text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeBank === 'kina' ? 'bg-[#B80E28] text-white' : 'text-white/50 hover:text-white'
                }`}
              >
                Kina Bank
              </button>
              <button
                onClick={() => setActiveBank('bsp')}
                className={`px-8 py-2.5 rounded-full text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeBank === 'bsp' ? 'bg-[#B80E28] text-white' : 'text-white/50 hover:text-white'
                }`}
              >
                BSP
              </button>
            </div>
          </div>

          {/* Kina Bank Content */}
          {activeBank === 'kina' && (
            <div>
              {/* Bank Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-20">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#B80E28]/15 rounded-xl">
                      <i className="ri-bank-line text-[#B80E28] text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white text-2xl font-extrabold">Kina Bank</h3>
                      <span className="text-[#B80E28] text-xs font-semibold tracking-widest uppercase">Official Banking Partner</span>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm leading-7 mb-5">
                    Kina Bank is one of Papua New Guinea&apos;s most trusted and established commercial banks, providing a full suite of financial services to individuals, businesses, and institutions across the country. Founded with a commitment to serving PNG communities, Kina Bank has grown into a cornerstone of the nation&apos;s financial infrastructure.
                  </p>
                  <p className="text-white/60 text-sm leading-7 mb-5">
                    As an official PNGGO banking partner, Kina Bank enables seamless, instant deposits and fast withdrawals directly in Papua New Guinea Kina (PGK). There are no currency conversion fees, no hidden charges, and no delays — just straightforward, reliable banking that PNG players can count on.
                  </p>
                  <p className="text-white/60 text-sm leading-7 mb-8">
                    Whether you prefer to bank via the Kina Bank mobile app, internet banking portal, or in-branch, all methods are fully supported for PNGGO transactions. Our integration with Kina Bank is built on a secure API connection that processes transactions in real time, 24 hours a day, 7 days a week.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {['Instant Deposits', 'Fast Withdrawals', 'Zero Fees', 'Mobile Banking', 'Secure API'].map((tag) => (
                      <span key={tag} className="bg-[#1a1a1a] border border-[#B80E28]/30 text-[#B80E28] text-xs font-semibold px-4 py-1.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Papua%20New%20Guinea%20Kina%20Bank%20modern%20banking%20app%20mobile%20phone%20digital%20finance%20technology%20dark%20background%20red%20accent%20professional%20photography%20clean%20minimal&width=600&height=480&seq=kina-bank-01&orientation=landscape"
                    alt="Kina Bank Mobile Banking"
                    className="w-full h-[360px] object-cover object-top rounded-2xl"
                  />
                  <div className="absolute -bottom-5 -right-5 bg-[#B80E28] text-white rounded-xl px-6 py-4 text-center">
                    <div className="text-xl font-extrabold">Instant</div>
                    <div className="text-xs text-white/80">Deposit Speed</div>
                  </div>
                </div>
              </div>

              {/* Kina Features Grid */}
              <div className="mb-20">
                <div className="text-center mb-10">
                  <h3 className="text-white text-2xl font-bold">Why Use Kina Bank with PNGGO?</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {kinaFeatures.map((f, i) => (
                    <div key={i} className="bg-[#111] border border-white/5 hover:border-[#B80E28]/40 rounded-xl p-7 transition-all duration-300 group">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#B80E28]/15 rounded-xl mb-5 group-hover:bg-[#B80E28]/25 transition-colors">
                        <i className={`${f.icon} text-[#B80E28] text-2xl`}></i>
                      </div>
                      <h4 className="text-white font-bold text-sm mb-3">{f.title}</h4>
                      <p className="text-white/50 text-xs leading-6">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Kina Deposit Steps */}
              <div className="bg-[#0d0d0d] rounded-2xl p-10 border border-white/5">
                <div className="text-center mb-10">
                  <h3 className="text-white text-2xl font-bold">How to Deposit via Kina Bank</h3>
                  <p className="text-white/50 text-sm mt-2">Follow these simple steps to fund your PNGGO account</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                  {depositStepsKina.map((s, i) => (
                    <div key={i} className="text-center relative">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#B80E28] rounded-full mx-auto mb-4 text-white font-extrabold text-sm">
                        {s.step}
                      </div>
                      {i < depositStepsKina.length - 1 && (
                        <div className="hidden lg:block absolute top-6 left-[calc(50%+24px)] right-0 h-px bg-[#B80E28]/30"></div>
                      )}
                      <h4 className="text-white font-bold text-sm mb-2">{s.title}</h4>
                      <p className="text-white/50 text-xs leading-5">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* BSP Content */}
          {activeBank === 'bsp' && (
            <div>
              {/* Bank Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-20">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#B80E28]/15 rounded-xl">
                      <i className="ri-building-2-line text-[#B80E28] text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white text-2xl font-extrabold">Bank of South Pacific</h3>
                      <span className="text-[#B80E28] text-xs font-semibold tracking-widest uppercase">Official Banking Partner</span>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm leading-7 mb-5">
                    The Bank of South Pacific (BSP) is the largest bank in the Pacific region, with a dominant presence across Papua New Guinea and 10 other Pacific Island nations. BSP serves millions of customers through its extensive branch network, ATMs, and digital banking platforms, making it the most accessible bank for PNG players.
                  </p>
                  <p className="text-white/60 text-sm leading-7 mb-5">
                    As a PNGGO banking partner, BSP provides a rock-solid payment infrastructure that supports instant deposits and rapid withdrawals in PGK. BSP&apos;s internet banking and mobile app are among the most widely used in PNG, meaning the vast majority of PNGGO players already have a BSP account ready to use.
                  </p>
                  <p className="text-white/60 text-sm leading-7 mb-8">
                    BSP&apos;s advanced fraud detection, two-factor authentication, and real-time transaction monitoring ensure that every payment made through PNGGO is protected to the highest standard. With BSP, you can deposit and withdraw with complete confidence, knowing your funds are in safe hands.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {['Pacific\'s Largest Bank', 'Instant Deposits', 'Zero Fees', 'BSP Mobile App', '2FA Security'].map((tag) => (
                      <span key={tag} className="bg-[#1a1a1a] border border-[#B80E28]/30 text-[#B80E28] text-xs font-semibold px-4 py-1.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Bank%20of%20South%20Pacific%20BSP%20Papua%20New%20Guinea%20digital%20banking%20secure%20online%20payment%20technology%20modern%20interface%20dark%20background%20professional%20corporate%20photography&width=600&height=480&seq=bsp-bank-01&orientation=landscape"
                    alt="BSP Bank Digital Banking"
                    className="w-full h-[360px] object-cover object-top rounded-2xl"
                  />
                  <div className="absolute -bottom-5 -right-5 bg-[#B80E28] text-white rounded-xl px-6 py-4 text-center">
                    <div className="text-xl font-extrabold">Pacific</div>
                    <div className="text-xs text-white/80">#1 Bank</div>
                  </div>
                </div>
              </div>

              {/* BSP Features Grid */}
              <div className="mb-20">
                <div className="text-center mb-10">
                  <h3 className="text-white text-2xl font-bold">Why Use BSP with PNGGO?</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {bspFeatures.map((f, i) => (
                    <div key={i} className="bg-[#111] border border-white/5 hover:border-[#B80E28]/40 rounded-xl p-7 transition-all duration-300 group">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#B80E28]/15 rounded-xl mb-5 group-hover:bg-[#B80E28]/25 transition-colors">
                        <i className={`${f.icon} text-[#B80E28] text-2xl`}></i>
                      </div>
                      <h4 className="text-white font-bold text-sm mb-3">{f.title}</h4>
                      <p className="text-white/50 text-xs leading-6">{f.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* BSP Deposit Steps */}
              <div className="bg-[#0d0d0d] rounded-2xl p-10 border border-white/5">
                <div className="text-center mb-10">
                  <h3 className="text-white text-2xl font-bold">How to Deposit via BSP</h3>
                  <p className="text-white/50 text-sm mt-2">Follow these simple steps to fund your PNGGO account</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                  {depositStepsBSP.map((s, i) => (
                    <div key={i} className="text-center relative">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#B80E28] rounded-full mx-auto mb-4 text-white font-extrabold text-sm">
                        {s.step}
                      </div>
                      {i < depositStepsBSP.length - 1 && (
                        <div className="hidden lg:block absolute top-6 left-[calc(50%+24px)] right-0 h-px bg-[#B80E28]/30"></div>
                      )}
                      <h4 className="text-white font-bold text-sm mb-2">{s.title}</h4>
                      <p className="text-white/50 text-xs leading-5">{s.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Side-by-Side Comparison ── */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionBadge text="Comparison" />
            <h2 className="text-white text-3xl md:text-4xl font-bold mb-4">Kina Bank vs BSP</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto">
              Both banks offer excellent service for PNGGO players. Here&apos;s a quick side-by-side comparison.
            </p>
          </div>
          <div className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 bg-[#1a1a1a] border-b border-white/5">
              <div className="p-5 text-white/40 text-xs font-bold uppercase tracking-widest">Feature</div>
              <div className="p-5 text-center border-l border-white/5">
                <div className="flex items-center justify-center gap-2">
                  <i className="ri-bank-line text-[#B80E28] text-lg"></i>
                  <span className="text-white font-bold text-sm">Kina Bank</span>
                </div>
              </div>
              <div className="p-5 text-center border-l border-white/5">
                <div className="flex items-center justify-center gap-2">
                  <i className="ri-building-2-line text-[#B80E28] text-lg"></i>
                  <span className="text-white font-bold text-sm">BSP</span>
                </div>
              </div>
            </div>
            {comparisonData.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 border-b border-white/5 last:border-b-0 ${i % 2 === 0 ? 'bg-transparent' : 'bg-[#1a1a1a]/30'}`}
              >
                <div className="p-4 text-white/60 text-xs font-medium">{row.feature}</div>
                <div className="p-4 text-center border-l border-white/5">
                  <span className="text-[#B80E28] text-xs font-semibold">{row.kina}</span>
                </div>
                <div className="p-4 text-center border-l border-white/5">
                  <span className="text-[#B80E28] text-xs font-semibold">{row.bsp}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security Section ── */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionBadge text="Security" />
              <h2 className="text-white text-3xl md:text-4xl font-bold mb-6 leading-tight">
                Your Money Is<br />
                <span className="text-[#B80E28]">Always Protected</span>
              </h2>
              <p className="text-white/60 text-sm leading-7 mb-6">
                PNGGO takes payment security extremely seriously. Every transaction processed through Kina Bank or BSP is protected by multiple layers of security, including 128-bit SSL encryption, real-time fraud monitoring, and strict KYC (Know Your Customer) verification.
              </p>
              <p className="text-white/60 text-sm leading-7 mb-8">
                We never store your banking credentials on our servers. All payment processing is handled directly by Kina Bank and BSP through their own certified, PCI-DSS compliant systems. Your financial data never passes through PNGGO&apos;s servers — it goes directly from your bank to your PNGGO wallet.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { icon: 'ri-shield-check-line', title: '128-Bit SSL', desc: 'Military-grade encryption on all transactions.' },
                  { icon: 'ri-eye-off-line', title: 'No Data Storage', desc: 'We never store your banking credentials.' },
                  { icon: 'ri-alarm-warning-line', title: 'Fraud Monitoring', desc: 'Real-time detection of suspicious activity.' },
                  { icon: 'ri-user-follow-line', title: 'KYC Verified', desc: 'All accounts are identity-verified for safety.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#B80E28]/15 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#B80E28] text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold mb-1">{item.title}</div>
                      <div className="text-white/50 text-xs leading-5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=cybersecurity%20digital%20payment%20protection%20shield%20glowing%20red%20neon%20dark%20background%20secure%20banking%20transaction%20encryption%20technology%20abstract%20visualization%20professional%20studio&width=600&height=480&seq=payment-security-01&orientation=landscape"
                alt="PNGGO Payment Security"
                className="w-full h-[380px] object-cover object-top rounded-2xl"
              />
              <div className="absolute top-5 right-5 bg-[#0a0a0a]/90 border border-[#B80E28]/40 rounded-xl px-5 py-4 text-center">
                <i className="ri-shield-check-fill text-[#B80E28] text-3xl"></i>
                <div className="text-white text-sm font-bold mt-1">PCI-DSS</div>
                <div className="text-white/50 text-xs">Compliant</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionBadge text="FAQ" />
            <h2 className="text-white text-3xl md:text-4xl font-bold mb-4">Payment Questions Answered</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto">
              Everything you need to know about depositing and withdrawing at PNGGO.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-[#111] border border-white/5 hover:border-[#B80E28]/30 rounded-xl overflow-hidden transition-all duration-300"
              >
                <button
                  className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-white text-sm font-semibold pr-4">{faq.q}</span>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-[#B80E28] text-lg`}></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-7 pb-6">
                    <p className="text-white/55 text-sm leading-7">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionBadge text="Get Started" />
          <h2 className="text-white text-3xl md:text-4xl font-bold mb-5">
            Ready to Make Your<br />
            <span className="text-[#B80E28]">First Deposit?</span>
          </h2>
          <p className="text-white/55 text-sm leading-7 mb-8 max-w-xl mx-auto">
            Join over 100,000 PNG players who trust PNGGO for fast, secure, and fee-free banking. Sign up today and make your first deposit via Kina Bank or BSP in under 2 minutes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://pnggo.bet/RFLANDINGPAGEPNG"
              className="inline-flex items-center gap-2 bg-[#B80E28] text-white font-bold px-10 py-3.5 rounded-full hover:bg-[#9a0b21] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Create Free Account <i className="fas fa-chevron-right text-xs"></i>
            </a>
            <a
              href="/about"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-10 py-3.5 rounded-full hover:border-[#B80E28] hover:text-[#B80E28] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Learn About PNGGO
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
